import type { RelatedSite } from './types';

/**
 * 関連事業 詳細(JA)前半: 全日本執事協会 / THE BUTLER / 執事ベル。
 * 2026-09-18 二版: 「各ページはそれぞれオリジナルで書く・類似ページと判定されない」(Yuichi)に従い、
 * 紹介先の公式本文の文をそのまま使わず(30字以上の連続一致 0 を自己検査)、学びの場の視点で書き下ろした。
 * 事実の出典(実読・ファイル:行):
 *  - 協会: site-japanbutlerassociation/src/pages/about/history/page.tsx:16-125(創立1890・田中慎吾・英国留学・1955分立・2000再統合・
 *    2023就任・名簿公開・2025-02-19法人化・135周年), about/overview/page.tsx:47-71(所在地・事業5・資格・古城の一室・名簿),
 *    about/access/page.tsx:14-16(電話・予約制), about/mission/page.tsx:46-51(使命・未来像), about/organization/page.tsx:66,133(専門資格・登録講師制度),
 *    about/activities/page.tsx:18-61(YouTube・セミナー), src/data/faq.json:103-111(四つの柱・試験の実施体制)
 *  - THE BUTLER: site-thebutler/src/pages/home/components/IntroSplit.tsx:19-23, PromiseCards.tsx:22-55, FlowSteps.tsx:16-40,
 *    BusinessPage.tsx:24-148(for Business・スポット〜年間・役務), CompanyPage.tsx(6事業・代表理事)
 *  - 執事ベル: site-butlerbell/src/pages/about/page.tsx:13-316(プロフィール・就任・法人化・活動4・理念4), wisdom/page.tsx:12-55(執事の知恵4分類・20年以上),
 *    media/page.tsx:8-18(媒体・執事監修), src/data/articles.json:522-740
 *  - 装い・書籍: site-queensbutler/docs/_prompts/20260916_gakken_attire_canon.md:13-48
 * 「三代目」は書かない(Gakken 2 刷対応)。「派遣」は使わない。
 */
const sitesA: RelatedSite[] = [
  // ═══════════════ 全日本執事協会 ═══════════════
  {
    slug: 'butler-association',
    name: '一般社団法人 全日本執事協会',
    role: '執事の育成と認定を担う運営母体',
    tagline:
      '明治23年に始まった、日本でいちばん古い執事の団体。研修と資格認定を通じて執事の「ものさし」を守り続けており、日本執事アカデミーのカリキュラムもこの団体の基準に沿って組まれています。',
    hubImage: { src: '/images/related/butler-association/jba-butler-profession-hero.jpg', alt: '執事という職業を紹介する協会サイトの一場面（協会サイトの画像）' },
    heroImage: { src: '/images/related/butler-association/jba-p-000-home-hero.webp', alt: '暖炉のある応接室で主家に付き従う執事（全日本執事協会の公式サイトのトップ画像）', position: 'center 35%' },
    heroTitle: '一般社団法人 全日本執事協会',
    heroTitleEn: 'ALL JAPAN BUTLER ASSOCIATION',
    heroLead:
      '明治23年の発足から数えて135年。執事を育て、技能を測り、認定するという仕事を担ってきたのがこの協会です。アカデミーの教室で交わされる一礼も、修了後に受ける試験も、すべてこの団体が定めた基準を土台にしています。学ぶ側の視点から、協会という組織を読み解きます。',
    officialUrl: 'https://butler-association.jp/',
    officialLabel: '協会の公式サイトを開く',
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'WHAT IT IS',
        title: '執事の「ものさし」を預かる団体',
        sub: 'The keeper of the standard',
        body: [],
        subs: [
          { t: '公的な免許がない職業', b: '執事という仕事には、国家資格のような公的な免許がありません。だからこそ、誰が執事を名乗れるのか、どの水準に達していれば一人前と言えるのかを決める「ものさし」が要ります。明治23年、すなわち1890年に創立者・田中慎吾が立ち上げたこの団体は、その物差しを預かり、135年にわたって更新し続けてきた組織です。' },
          { t: '英国の流儀を、日本の家に合わせる', b: '田中慎吾は英国に学び、向こうの館で受け継がれてきた執事の流儀を持ち帰りました。ただし、それをそのまま日本に移植したわけではありません。西洋の給仕作法に日本の礼節を重ね、日本の暮らしに合う執事像を一から組み立てる。この編集作業こそが協会の出発点であり、いまアカデミーの教科書に載っている所作の多くは、ここに源をもっています。' },
          { t: 'アカデミーは「基準を教える場」', b: '受講を考えている方に最初に知っておいてほしいのは、アカデミーが「協会の基準を教える場」だという点です。授業の到達目標、実技試験の採点、修了後の認定。どれも協会が決めた枠組みの中にあります。協会を知ることは、自分がこれから何を目指して学ぶのかを知ることと同じです。' },
          { t: '掲げている使命', b: '執事の職業意識と心構えを世の中に広め、接客業全体の水準を引き上げること。協会はこれを自らの役目としています。' },
          { t: '思い描く将来', b: '執事が敬意をもって迎えられる専門職として定着し、さまざまな場で働いている社会。アカデミーが「基準で評価される専門職に」と掲げる理念は、この将来像の教育版だと言えます。' },
        ],
        image: { src: '/images/related/butler-association/jba-mission-training-butlers-01.jpg', alt: '執事を育てるという協会の使命を表す一場面（協会サイトの画像）' },
      },
      {
        kind: 'steps',
        label: 'FOUR CHAPTERS',
        title: '創立・分立・再統合・法人化——四つの章で読む沿革',
        sub: '135 years in four chapters',
        lede:
          '協会の歩みは平坦ではありませんでした。意見が割れて二つに分かれた時代があり、それをまとめ直した世代があり、そして法人として体制を整え直した最近の章があります。アカデミーで教わる「全国共通の基準」は、この四つの章を通り抜けて残ったものです。',
        image: { src: '/images/related/butler-association/jba-history-founding-butler-01.jpg', alt: '創立期の執事を伝える協会サイトの沿革の一場面（協会サイトの画像）' },
        steps: [
          {
            title: '第一章——明治の創立と、最初のカリキュラム',
            desc: '文明開化のただなかで田中慎吾が協会を興し、日本で初めての体系立った執事養成の課程を開きました。英国から指導者を招き、向こうの給仕作法と日本流の礼儀を一つの教程にまとめたのがこの時期です。',
          },
          {
            title: '第二章——1955年、東西に分かれる',
            desc: '育て方の方針をめぐって意見が割れ、協会は関東と関西の二つに分かれました。それから四十年あまり、東西はそれぞれのやり方で執事を育て、土地ごとの基準を築いていきます。',
          },
          {
            title: '第三章——2000年、二代目頭取のもとで一つに',
            desc: '二代目頭取・山田和彦が東西の作法を一つずつ突き合わせ、2000年に再び一つの全国組織に戻しました。このとき生まれたのが、地域を問わず通用する品質基準と教育の仕組みです。アカデミーの三つのコースが依拠している「共通の物差し」はここから始まります。',
          },
          {
            title: '第四章——2023年からの新章と、2025年の法人化',
            desc: '2023年、石井裕一（執事ベル）が頭取となり、在籍執事の名簿を公開し、料金の見える化と、紹介なしでも相談できる窓口づくりに踏み切りました。2025年2月19日には一般社団法人として登記し、石井裕一が代表理事に。同じ年、協会は創立から135年の節目を迎えています。',
          },
        ],
      },
      {
        kind: 'cards',
        label: 'FIVE FIELDS',
        title: '協会が手がける五つの領域',
        sub: 'What the association actually does',
        lede:
          '協会の仕事は「育てる」「認める」「伝える」「つなぐ」「残す」の五つに分けて考えると理解しやすくなります。アカデミーはこのうち「育てる」を教室の形で引き受け、修了生は「認める」を経て社会に出ていきます。',
        cards: [
          {
            title: '育てる——研修と実地の稽古',
            desc: '基礎の所作から専門的な技術までを段階的に積み上げる研修課程を運営。お台場の執事サロンを稽古場として、身体で覚える教育を続けています。アカデミーの授業も同じ稽古場の延長にあります。',
            image: { src: '/images/related/butler-association/jba-activities-butler-training-practice-ja-01.jpg', alt: '協会の執事研修の実技（協会サイトの画像）' },
          },
          {
            title: '認める——三段階の資格と専門資格',
            desc: '技能の到達度をアソシエイト、プロフェッショナル、マスターの三段に分けて認定。これに加えて食事、旅行、ビジネスマナーといった分野別の専門資格も用意されています。試験は各地の主要都市で定期的に開かれ、筆記と実技の両方で判定されます。',
            image: { src: '/images/related/butler-association/jba-std-certification-01.jpg', alt: '協会の資格認定を示す一場面（協会サイトの画像）' },
          },
          {
            title: '伝える——企業・個人向けのセミナー',
            desc: 'リーダーシップ、ホスピタリティ、コミュニケーションといったテーマで、企業や個人に向けた実践講座を開いています。執事の立ち居振る舞いを、接客業や企業の現場で使える形に翻訳して届ける仕事です。',
            image: { src: '/images/related/butler-association/jba-organization-training-education-01.jpg', alt: '協会の教育研修の一場面（協会サイトの画像）' },
          },
          {
            title: 'つなぐ——海外との交流',
            desc: '海外からの来客に対応できる執事を育て、世界の執事文化と行き来する窓口を開いています。国際的に通用する執事を送り出すことも、協会が掲げる目標のひとつです。',
            image: { src: '/images/related/butler-association/jba-mission-international-guests-01.jpg', alt: '海外からの来客を迎える協会の執事（協会サイトの画像）' },
          },
          {
            title: '残す——出版と発信',
            desc: '書籍や刊行物、SNS、YouTubeの「執事ベル」を通じて、執事文化の現在を記録し、社会に向けて発信しています。Gakkenから出た『執事のマナーと教養図鑑』の監修も、この領域の仕事です。',
            image: { src: '/images/related/butler-association/jba-publishing-media-materials-01.jpg', alt: '出版・情報発信の資料（協会サイトの画像）' },
          },
          {
            title: '届ける——執事サービスの運営',
            desc: '協会自身が「THE BUTLER」という執事サービスを運営し、数時間の付き添いから長期の契約まで、家庭と法人の両方に執事を届けています。アカデミーの修了生が実際に働く現場です。',
            image: { src: '/images/related/butler-association/jba-service-footman-guest-reception-01.jpg', alt: '協会の執事サービスで来客を迎える場面（協会サイトの画像）' },
          },
        ],
        tint: true,
      },
      {
        kind: 'twocol',
        label: 'THREE TIERS',
        title: '三段の資格が、そのまま学びの階段になる',
        sub: 'Associate / Professional / Master',
        body: [],
        subs: [
          { t: '段階ごとに測るための仕組み', b: '協会の資格制度は、執事の知識と技能を段階ごとに測るための仕組みです。厳しい基準で公平に判定することで、業界全体の水準を底上げし、執事という専門職の輪郭をはっきりさせることを狙いとしています。' },
          { t: '三段の階段は、コース設計と重なる', b: 'アカデミーにとって大切なのは、この三段の階段がコース設計とそのまま重なっていることです。エッセンシャルはアソシエイトを、アドバンスはプロフェッショナルを、マスターは同名の最上位資格を見据えて組まれています。受講生は、自分がいまどの段にいて、次の段に上がるには何が足りないのかを、協会の基準に照らして具体的に確かめられます。' },
          { t: '試験は筆記だけでは終わらない', b: '試験は年に何度か主要都市で行われ、筆記だけでなく実技も課されます。教室で何度も繰り返す紅茶の給仕やトレイの運び方は、単なる稽古ではなく、この実技試験で問われることそのものです。' },
        ],
        image: { src: '/images/related/butler-association/jba-become-professional-practice.jpg', alt: 'プロフェッショナル段階の実践（協会サイトの画像）' },
        reverse: true,
      },
      {
        kind: 'twocol',
        label: 'HOW IT RUNS',
        title: '登録講師と専門家が支える運営',
        sub: 'People behind the association',
        body: [],
        subs: [
          { t: '責任者が横につながる運営', b: '協会の運営は、分野ごとの責任者が横につながる形で進められています。全体の方針を決め、国内外の関係機関との窓口を務めるのが代表理事。それを補佐して各事業の調整や監督にあたるのが副理事長です。' },
          { t: '日々を動かす四つの仕組み', b: '日々の活動を動かしているのは、登録講師の仕組み、外部の専門家による助言、海外の団体との提携、そして業務委託の体制です。アカデミーで教壇に立つ講師陣も、この登録講師の枠組みの中で協会の基準を共有しています。どの教室で誰に習っても同じ水準の指導が受けられるのは、そのためです。' },
          { t: '活動の四本柱と、教室の位置', b: '活動の柱は四本。資格の認定、教育と研修の課程、会員に向けた支援、そして調査と研究です。アカデミーは二本目の柱を担う教育機関であり、修了生は会員支援や研究の成果に触れながら、執事としての経験を重ねていきます。' },
        ],
        image: { src: '/images/related/butler-association/jba-butler-team-briefing.jpg', alt: '執事チームの打ち合わせ（協会サイトの画像）' },
        tint: true,
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/butler-association/jba-history-inherited-service-skill-01.jpg', alt: '受け継がれてきた給仕の技能（協会サイトの画像）' },
          { src: '/images/related/butler-association/jba-history-team-quality-standards-ja-01.jpg', alt: '全国統一の品質基準を担う執事たち（協会サイトの画像）' },
          { src: '/images/related/butler-association/std1-deportment-standing.jpg', alt: '立ち姿の基準（協会サイトの画像）' },
          { src: '/images/related/butler-association/std1-grooming-attire.jpg', alt: '身だしなみと装いの基準（協会サイトの画像）' },
        ],
      },
      {
        kind: 'twocol',
        label: 'THE SALON',
        title: 'お台場に構える、古城の一室のような執事サロン',
        sub: 'Headquarters in Odaiba',
        body: [],
        subs: [
          { t: '本部はお台場の青海に', b: '本部は東京・お台場の青海にあります。東京湾を見渡す、海外からの来客も多い一角です。ここに協会は、中世ヨーロッパの城の一室を思わせる内装の事務所を置き、「執事サロン」と呼んでいます。' },
          { t: '見学室ではなく、稽古のための空間', b: 'サロンは見学のための展示室ではなく、稽古のための空間です。銀器の並べ方、紅茶を注ぐ角度、来客を迎える立ち位置。教科書で読んだことを、本物の家具や調度に囲まれて身体に覚え込ませる。アカデミーの受講生が実技を磨く教室も、この場所です。' },
          { t: '訪問は事前予約から', b: '訪問は事前予約が必要です。資格や協会について相談したい方は、まず連絡を入れてから足を運んでください。' },
          { t: '所在地', b: '〒135-0064 東京都江東区青海2丁目7−4' },
          { t: '連絡先', b: '03-6824-4086（見学・来訪はすべて予約制）' },
        ],
        image: { src: '/images/related/butler-association/jba-access-aomi-office-exterior-01.jpg', alt: 'お台場・青海の協会事務所の外観（協会サイトの画像）' },
        reverse: true,
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE IT',
        title: '学びの場から見た、協会という存在',
        sub: 'The association, seen from the academy',
        body: [],
        subs: [
          { t: '運営母体としての協会', b: 'アカデミーは協会を運営母体とする教育機関です。三つのコースは三段の資格に対応し、修了生は協会の認定を受けてから執事として働き始めます。もし協会の基準がなければ、アカデミーの授業は「何のために練習しているのか」を失ってしまいます。' },
          { t: '理念を、教室の言葉に置き換える', b: '見方を変えれば、アカデミーは協会の理念を教室の言葉に置き換える場所でもあります。「執事の心構えを社会に広める」という抽象的な使命は、扉を開ける角度や、トレイを持つ高さといった、目に見える形になって初めて受講生に届きます。その翻訳を毎日行っているのがアカデミーです。' },
          { t: '入学前に、まず協会を知る', b: '入学を検討している方には、まず協会がどんな団体で、どんな基準で執事を認めているのかを知っていただきたい。公式サイトには沿革、資格制度、活動の詳細が載っています。そのうえでアカデミーのコース案内に戻ってくると、授業の一つひとつの意味が違って見えるはずです。' },
        ],
        image: { src: '/images/related/butler-association/jba-become-master-leadership.jpg', alt: 'マスター段階のリーダーシップを示す一場面（協会サイトの画像）' },
      },
      {
        kind: 'cards',
        label: 'AT EACH STAGE',
        title: '入学前・在学中・修了時・修了後——協会との四つの接点',
        sub: 'Four touchpoints for learners',
        lede: '協会とアカデミーの関係を、受講生の時間軸に沿って並べ直しました。自分がどの段階で協会と関わることになるのかを、あらかじめ思い描いておいてください。',
        cards: [
          { title: '入学前——基準を知る', desc: '協会の資格制度と使命を読んでおくと、コース案内に書かれた到達目標の意味が分かります。入学相談の前に、公式サイトの「協会について」を一度眺めてみてください。' },
          { title: '在学中——基準で稽古する', desc: '各コースの授業は協会の認定基準を逆算して組まれています。講師が「ここは試験で見られる」と言う箇所は、協会の採点項目そのものです。' },
          { title: '修了時——基準で測られる', desc: '筆記と実技の試験を受け、協会から認定を受けます。認定証は、学んだ技能が第三者の基準に達していることの証明になります。' },
          { title: '修了後——基準のもとで働く', desc: '協会が運営する執事サービスをはじめ、家庭や法人の現場で、認定を受けた執事として仕事を始めます。基準は働き始めてからも、自分を測り直す物差しであり続けます。' },
        ],
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: '協会の言葉を、教室の言葉に置き換える',
        sub: 'Four terms you will hear in class',
        lede: '協会のサイトに出てくる言葉は、アカデミーの授業でもそのまま使われます。入学前に意味をつかんでおくと、講師の説明が聞き取りやすくなります。',
        cards: [
          {
            title: '頭取',
            desc: '協会の長の呼び名です。法人としての肩書は代表理事で、いまは石井裕一（執事ベル）が務めています。授業で「頭取」と言えば、この人を指します。',
            image: {
              src: '/images/related/butler-association/jba-std-hub-01.jpg',
              alt: '協会の基準を示す一場面（協会サイトの画像）'
            }
          },
          {
            title: '認定',
            desc: '協会が技能を測って認める仕組みのこと。三段階の資格と分野別の専門資格があり、アカデミーの各コースは前者に対応しています。',
            image: {
              src: '/images/related/butler-association/jba-std-hub-02.jpg',
              alt: '協会の認定基準を示す一場面（協会サイトの画像）'
            }
          },
          {
            title: '執事サロン',
            desc: 'お台場・青海の本部に設けられた、古城の一室を思わせる稽古場です。アカデミーの実技もここで行われます。見学は予約制。',
            image: {
              src: '/images/related/butler-association/jba-std-hub-03.jpg',
              alt: '協会の執事サロンでの稽古（協会サイトの画像）'
            }
          },
          {
            title: '基準',
            desc: '協会が公開している執事の行動規範のこと。所作、身だしなみ、守秘、危機対応といった項目ごとにまとめられており、授業の到達目標の根拠になっています。',
            image: {
              src: '/images/related/butler-association/jba-std-hub-06.jpg',
              alt: '協会が公開する執事の基準（協会サイトの画像）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '公式サイトは、この順番で読むと迷わない',
        sub: 'A reading order for prospective students',
        body: [
          '協会の公式サイトは情報量が多く、初めて開くと何から読めばよいか迷います。アカデミーの入学相談では、「協会について」→「沿革」→「資格認定」→「基準」の順で読むことを勧めています。団体の成り立ちを知り、次に自分が受ける試験の枠組みを知り、最後に日々の行動規範を確かめる、という順番です。',
          '沿革のページには、創立から法人化までの節目が年表で並んでいます。ここを先に読んでおくと、なぜ全国共通の基準が2000年から始まるのか、なぜ2023年に名簿が公開されたのかが、時系列でつながります。',
          '資格認定と基準のページは、授業で講師が「ここは試験で見られる」と言う箇所の出典です。受講中に何度も戻ってくることになるので、ブックマークしておくことを勧めています。'
        ],
        image: {
          src: '/images/related/butler-association/jba-history-modern-butler-service-ja-01.jpg',
          alt: '現代の執事サービスを紹介する協会サイトの一場面（協会サイトの画像）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師が協会をどう見ているか',
        sub: 'The association through an instructor eyes',
        body: [
          'アカデミーの講師陣は、協会の登録講師の枠組みの中で教えています。彼らにとって協会は「上の組織」というより、自分たちが教える内容の出どころです。授業で使う所作の一つひとつに、協会の基準という裏づけがあるからこそ、講師は迷わず教えることができます。',
          '講師がよく口にするのは、「基準があるから厳しくできる」という言葉です。個人の好みで採点するのではなく、協会が定めた項目に照らして評価する。受講生にとっても、何を直せば合格に近づくのかがはっきりします。',
          '一方で講師は、基準が「上限」ではなく「下限」であることも強調します。協会の基準を満たすことは出発点であり、その先の工夫や気配りは、現場で一人ひとりが積み上げていくものです。'
        ],
        image: {
          src: '/images/related/butler-association/jba-butler-quality-control.jpg',
          alt: '執事の品質を確かめる場面（協会サイトの画像）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: 'よくある取り違えを、先に解いておく',
        sub: 'Common misreadings about the association',
        body: [
          '第一の取り違えは、「協会が学校である」というものです。学校はアカデミーで、協会は基準を定め、認定し、執事サービスを運営する団体です。入学の申し込みはアカデミーへ、資格試験の要項は協会へ、と窓口が分かれています。',
          '第二は、「資格を取れば自動的に協会の執事として働ける」という理解です。認定は技能の証明であって、就業の約束ではありません。協会が運営する執事サービスで働くには、認定を受けたうえで別途の手続きが要ります。',
          '第三は、「協会は昔からずっと一つの組織だった」という思い込みです。実際には東西に分かれていた四十年あまりがあり、2000年に再統合されています。この歴史を知っていると、全国共通の基準の重みが違って見えます。'
        ],
        image: {
          src: '/images/related/butler-association/std1-conduct-guest-staff-flow.jpg',
          alt: '来客と執事の動線を示す協会の基準の一場面（協会サイトの画像）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'THE STANDARD IN PRACTICE',
        title: '「基準」という一文が、採点表に降りてくるまで',
        sub: 'How a written standard becomes a grade',
        body: [],
        subs: [
          { t: '文章のままでは教室で使えない', b: '協会が定める基準は、文章のままでは教室で使えません。アカデミーが最初にするのは、その一文を、誰が見ても同じ判定になる形にほどく作業です。たとえば「静かに運ぶ」という一語は、足音、盆の高さ、視線の置き方、卓に触れる瞬間の速さという四つの見どころに分けて、はじめて講師が同じ目で見られるようになります。' },
          { t: '見どころが、そのまま稽古の順番になる', b: 'ほどいた見どころは、そのまま稽古の順番になります。一度に全部を直させると受講生は動けなくなるので、今日は足音だけ、次は盆の水平だけ、と一つずつ渡していきます。四つがそろった日に、その所作はようやく基準を満たしたと判断する。採点表は、この積み上げを記録しておくための紙にすぎません。' },
          { t: '合格線が講師で動かない利点', b: '基準を持つ団体が教室の外にあることの利点は、その日の講師の好みで合格線が動かない点にあります。担当が替わっても期が替わっても、見る場所は同じ。受講生には「誰に習っても同じところを見られる」という安心になり、教える側には、自分の癖を持ち込まないための歯止めになります。' },
        ],
        image: { src: '/images/related/butler-association/jba-std-hospitality-01.jpg', alt: '基準に沿ってもてなしの所作を確かめる場面（全日本執事協会の公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'official',
        title: '全日本執事協会の公式サイト',
        body: '沿革の詳細、資格制度の要項、会員向けの案内、見学や取材の申し込みは公式サイトで。アカデミーの受講相談は、当サイトのお問い合わせからどうぞ。',
        url: 'https://butler-association.jp/',
        urlLabel: 'butler-association.jp',
      },
    ],
    faq: [
      { q: 'アカデミーと協会は、どちらが「上」なのですか。', a: '協会が運営母体で、アカデミーはその教育部門にあたります。授業の到達目標や修了後の認定は、すべて協会の基準に基づいています。' },
      { q: '創立から何年になりますか。', a: '1890年（明治23年）の創立で、2025年に135年を迎えました。同じ2025年の2月19日に一般社団法人として登記されています。' },
      { q: '資格は何段階ありますか。分野別のものもありますか。', a: 'アソシエイト、プロフェッショナル、マスターの三段階です。加えて食事・旅行・ビジネスマナーなど分野ごとの専門資格があります。' },
      { q: '試験はどこで、どのように行われますか。', a: '主要都市で定期的に実施され、筆記と実技の両方で判定されます。アカデミーの実技稽古は、この試験を前提に組まれています。' },
      { q: '本部のサロンは見学できますか。', a: '東京都江東区青海2丁目7−4の本部は予約制です。見学や相談を希望する場合は、03-6824-4086 に事前に連絡してください。' },
      { q: '企業の研修も頼めますか。', a: 'はい。協会はリーダーシップやホスピタリティをテーマにした企業向け講座を開いています。アカデミーの法人研修と併せて検討できます。' },
      {
        q: '協会の「基準」は受講生も読めますか。',
        a: 'はい。協会の公式サイトで公開されており、アカデミーの授業でも到達目標の根拠として参照します。入学前に一読しておくと、授業の意図がつかみやすくなります。'
      },
      {
        q: '協会の会員になると何が変わりますか。',
        a: '協会は会員向けの支援を活動の柱の一つとしています。内容と手続きは協会の公式サイトで案内されており、アカデミーの窓口では扱っていません。'
      },
    ],
  },

  // ═══════════════ THE BUTLER ═══════════════
  {
    slug: 'the-butler',
    name: 'THE BUTLER',
    role: '協会が運営する執事サービス',
    tagline:
      '協会が自ら運営する執事のサービス。家の切り盛りから経営者の右腕、記念日や式典のもてなしまで、認定を受けた執事が家庭と法人の両方に仕えます。アカデミーの修了生が働く現場のひとつです。',
    hubImage: { src: '/images/related/the-butler/hero-home-revised-2026.jpg', alt: '正餐の間で食卓を整えるTHE BUTLERの執事たち（THE BUTLER の画像）' },
    heroImage: { src: '/images/related/the-butler/hero-home-revised-2026.jpg', alt: '正餐の間で食卓を整えるTHE BUTLERの執事たち（THE BUTLER の画像）', position: 'center 35%' },
    heroTitle: 'THE BUTLER',
    heroTitleEn: 'BUTLER SERVICE RUN BY THE ASSOCIATION',
    heroLead:
      '協会が育て、認めた執事を、実際に家庭や会社へ届けているのがTHE BUTLERです。アカデミーの教室で繰り返す所作は、最終的にこのサービスの現場で試されます。修了生がどんな場面で、どんなふうに働いているのか。学びの延長線としてのTHE BUTLERを紹介します。',
    officialUrl: 'https://thebutler.jp/',
    officialLabel: 'THE BUTLERの公式サイトを開く',
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'WHAT IT IS',
        title: '家事代行でもメイドでもない、「執事」を届けるサービス',
        sub: 'What sets it apart',
        body: [],
        subs: [
          { t: '家庭と法人の両方が対象', b: 'THE BUTLERは、協会が運営する執事のサービスです。対象は家庭と法人の両方。台所や洗濯といった家事はもちろん、来客の応対や秘書のような仕事、車の手配、会社での接遇まで、暮らしと仕事の隅々が守備範囲になります。' },
          { t: '家事代行やメイドとの違い', b: 'ただし、家事代行やメイドとは名乗り方も中身も違います。違いを生むのは、家の生活のリズムを把握したうえで、頼まれる前に整え、頼まれない間は姿を消しているという姿勢です。アカデミーの授業で最も時間を割いているのも、この「先回り」と「控え」の身体感覚を養うことです。' },
          { t: '無料相談から当日まで', b: '依頼は無料相談から始まり、執事に任せたい日時と場面を伝えるだけで動き出します。専属の担当者が場面に合う執事と契約の形を提案し、当日は執事服を着た執事が時間どおりに静かに現れる。学びの側から見ると、この「静かに現れる」までの準備こそが授業の中身です。' },
        ],
        image: { src: '/images/related/the-butler/b08-partc-home-intro-welcome-v8-20260913.jpg', alt: '玄関で来客を迎えるTHE BUTLERの執事（THE BUTLER の画像）' },
      },
      {
        kind: 'cards',
        label: 'THREE FIELDS',
        title: '家・仕事・晴れの日——執事が働く三つの領域',
        sub: 'Home, work, occasions',
        lede:
          'THE BUTLERは自らの仕事を三つの領域に整理しています。アカデミーのエッセンシャル、アドバンス、マスターの各コースは、ちょうどこの三つの現場に順番に対応するように組まれています。',
        cards: [
          {
            title: '家を整える',
            desc: '掃除、洗濯、料理から、子どもの世話や日々の買い物まで。家の生活リズムを飲み込んだ専属の執事が、手の回らない部分をハウスキーピングの知識ごと引き受けます。エッセンシャルコースで学ぶ基礎所作が、そのまま毎日の仕事になる領域です。',
            image: { src: '/images/related/the-butler/b08-partc-home-promise-household-v8-20260913.jpg', alt: '家政を担う執事の一場面（THE BUTLER の画像）' },
          },
          {
            title: '仕事を支える',
            desc: '秘書業務、経費の整理、出張の段取り、会議の下準備、送迎。経営者のまわりに生じる雑事を引き取って、本人が判断に集中できる環境をつくります。業種をまたいで経験を積んだ執事が右腕を務める領域で、アドバンスコースの段取り力が活きます。',
            image: { src: '/images/related/the-butler/b08-partc-home-promise-business-v8-20260913.jpg', alt: '経営者を支える執事の一場面（THE BUTLER の画像）' },
          },
          {
            title: '晴れの日を仕立てる',
            desc: '誕生日、結婚式、会社の式典。会場の設営から来客の出迎え、給仕、進行、マナーの講話まで一貫して担います。燕尾服で臨む格式の高い場面で、マスターコースのプロトコルと危機対応が支えになります。',
            image: { src: '/images/related/the-butler/b08-partc-home-promise-occasion-v8-20260913.jpg', alt: '晴れの日の給仕にあたる燕尾服の執事（THE BUTLER の画像）' },
          },
        ],
        tint: true,
      },
      {
        kind: 'twocol',
        label: 'FOR COMPANIES',
        title: '受付の一礼が、会社の印象を決める',
        sub: 'THE BUTLER for Business',
        body: [],
        subs: [
          { t: 'for Business という枠', b: '法人向けには「THE BUTLER for Business」という枠があります。来客の受付、会議室での給茶、レセプションでの給仕。企業の現場で執事の所作が果たす役割は、目に見える業務量よりずっと大きい、というのがTHE BUTLERの考え方です。名刺を差し出す手、茶を置く音、立ち姿。細部の積み重ねが「この会社は違う」という確信を来客に残します。' },
          { t: '一日から年間まで、契約の幅', b: '契約は一日限りのスポットから年間契約まで幅があり、受付やドアマン、給仕、ティーバトラー、VIPイベントの接客、出張してのマナー指導と、役務も広く用意されています。共通しているのは、協会に所属する執事が担当するという一点です。' },
          { t: '法人研修は現場の所作を渡す', b: 'アカデミーの法人研修は、この現場で求められている所作を企業のスタッフに伝えるためのものです。ホテル、レジデンス、金融、ラグジュアリー販売、クリニック、自動車、ショーファーと業種別にプログラムを分けているのは、THE BUTLERが法人の現場で積み上げてきた経験を、そのまま研修の設計に写しているからです。' },
          { t: '家庭で培った信頼が、会社の現場でも通用する', b: '資産に関わる情報を扱う家庭で鍛えられた守秘の意識と、正装への誇り。この二つを兼ね備えた執事だからこそ、法人の場でも同じ品格で仕えられます。' },
        ],
        image: { src: '/images/related/the-butler/b09-business-reception-doorman.jpg', alt: '企業の受付でドアマンを務める執事（THE BUTLER の画像）' },
        reverse: true,
      },
      {
        kind: 'steps',
        label: 'HOW A REQUEST MOVES',
        title: '相談から当日まで、四つの段階',
        sub: 'From the first call to the day',
        lede:
          '依頼の流れは四段階に整理されています。どの段階でも、依頼する側は希望を伝えるだけで済むように設計されているのが特徴です。アカデミーの修了生は、この流れのいちばん最後——約束の時間に現れる執事——として現場に立ちます。',
        image: { src: '/images/related/the-butler/day-butler-schedule-review-v7-20260907-e.jpg', alt: '当日の予定を確かめるデイ・バトラー（THE BUTLER の画像）' },
        steps: [
          { title: '相談する', desc: '協会の窓口に、希望する日時と、執事に任せたい場面を伝えます。家庭の話でも事業の話でも構いません。相談に費用はかかりません。' },
          { title: '執事と契約の形を選ぶ', desc: '専属の担当者が、伝えられた場面に合う執事と契約プランを提示します。一日単位のデイ・バトラーか、専属のプライベート・バトラーか。候補の中から選ぶだけです。' },
          { title: '当日までを担当者が組み立てる', desc: '会場、進行、執事の装いといった細部を、担当者が作法に照らして詰めていきます。依頼側が段取りを考える必要はありません。' },
          { title: '当日、執事が現れる', desc: '約束の時刻に、協会の執事が静かに控えます。先回りして整え、必要のない間は引いている。教室で鍛えた所作が、ここで初めてお客様の前に出ます。' },
        ],
      },
      {
        kind: 'twocol',
        label: 'CHOOSE YOUR BUTLER',
        title: '名簿から執事を選べる、という新しさ',
        sub: 'A published roster',
        body: [],
        subs: [
          { t: '名簿が公開されている', b: 'THE BUTLERの特徴のひとつは、在籍している執事の名簿が公開されていて、依頼する側が執事を選べることです。長いあいだ協会は、執事は表に出ず、信頼できる紹介を通じてしか客と会わないという流儀を守ってきました。2023年に頭取となった石井裕一（執事ベル）は、この流儀を内側から改め、名簿の公開と料金の明示、紹介なしで相談できる窓口の設置に踏み切りました。' },
          { t: '名簿には得意な場面が書いてある', b: '名簿に載っている執事には、それぞれ得意な場面と経歴があります。家庭の家政に強い執事、業種を横断して経営者を支えてきた執事、晩餐会の給仕や進行を数多くこなした執事。依頼する側は、担当者と相談しながら場面に合う一人を選ぶことができます。' },
          { t: '受講生には、目に見える目標', b: 'アカデミーの受講生にとって、この名簿は目に見える目標です。協会の認定を受け、自分の名前と得意な場面を掲げて、依頼者から選ばれる執事になる。教室での稽古は、その日のためにあります。' },
        ],
        image: { src: '/images/related/the-butler/hero-usecase-listing-v2.jpg', alt: '利用場面ごとに控えるTHE BUTLERの執事（THE BUTLER の画像）' },
        tint: true,
      },
      {
        kind: 'cards',
        label: 'FOUR SCENES',
        title: '教室の稽古が、そのまま現場になる四つの場面',
        sub: 'Where the lessons are tested',
        lede: 'THE BUTLERが紹介している利用場面のうち、アカデミーの授業と一対一で対応するものを四つ選び、どのコースの何が使われるのかを添えました。',
        cards: [
          { title: '邸宅の日常', desc: '家事の段取り、食卓の準備、来客の応対。エッセンシャルコースで身につける基礎の所作が、そっくりそのまま毎日の業務になります。' },
          { title: '経営者のそば', desc: '秘書業務、出張手配、会議の下準備、送迎。アドバンスコースで鍛える段取りと言葉遣いが問われる場面です。' },
          { title: '記念日と晩餐', desc: '会場づくりから給仕、進行まで。燕尾服で立つ格式の場では、マスターコースで学ぶプロトコルと不測の事態への対応が頼りになります。' },
          { title: '会社の受付', desc: '受付やドアマン、給茶、VIPイベントの接客。アカデミーの法人研修と同じ基準で、会社の「顔」として振る舞います。' },
        ],
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE IT',
        title: '学びの場から見た、THE BUTLERという現場',
        sub: 'The service, seen from the academy',
        body: [],
        subs: [
          { t: '同じ協会のもとにある', b: 'THE BUTLERとアカデミーは、同じ協会のもとにあります。アカデミーが育て、協会が認め、THE BUTLERが届ける。この三つが一つの組織の中でつながっているので、教室で教える所作と現場で求められる所作のあいだに、ずれがありません。' },
          { t: '現場の出来事が、授業に戻ってくる', b: 'アカデミーの講師は、THE BUTLERの現場で起きた出来事を授業に持ち帰ります。予定より早く着いた来客にどう動いたか、食卓で銀器が一本足りないと気づいたときに何を優先したか。教科書には載っていない判断を学べるのは、このつながりがあるからです。' },
          { t: '頼みたい人と、働きたい人へ', b: '執事を頼みたい方はTHE BUTLERの公式サイトへ。執事として働きたい方はアカデミーのコース案内へ。入口は違っても、たどり着く先は同じ協会の執事文化です。' },
        ],
        image: { src: '/images/related/the-butler/b09-home-night-residence-attendance-v2-20260913.jpg', alt: '夜の邸宅で控える執事（THE BUTLER の画像）' },
      },
      {
        kind: 'twocol',
        label: 'FROM CLASS TO CLIENT',
        title: '教室で百回繰り返した一礼が、初めてお客様の前に出る日',
        sub: 'What graduates carry into the field',
        body: [],
        subs: [
          { t: '何百回と繰り返す動作', b: 'アカデミーでは、来客を迎える動作を何十回、何百回と繰り返します。扉を開く角度、腰を折る深さ、上着を受け取る手の位置。ひとつひとつは小さなことですが、THE BUTLERの現場では最初の数秒が、その日一日の印象を決めてしまいます。' },
          { t: '場面ごとに稽古する所作', b: '紅茶を注ぐ、ナプキンを扱う、アイロンをかける、車の乗り降りを支える、雨の日に傘を差し掛ける。場面ごとに稽古する所作は、THE BUTLERが家庭や式典で引き受けている仕事そのものです。学びと実務のあいだに翻訳は要りません。' },
          { t: '「基準で学んだ」という事実', b: '修了生が現場に立つとき、「協会の基準で学んだ」という事実が、依頼者の信頼を支えます。その信頼に応えられる執事だけを送り出すこと。アカデミーはそれを自分の責任だと考えています。' },
        ],
        image: { src: '/images/related/the-butler/napkin-seating-assistance-v7-20260907-f2.jpg', alt: '着席の介添えとナプキンの所作（THE BUTLER の画像）' },
        reverse: true,
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: 'THE BUTLERの言葉を、学びの言葉で読む',
        sub: 'Four terms to know before you enquire',
        lede: 'THE BUTLERの公式サイトには、契約や役務を表す固有の言葉が出てきます。アカデミーの授業と対応づけて整理しました。',
        cards: [
          {
            title: 'デイ・バトラー',
            desc: '一日単位で執事を迎える契約の形です。初めての依頼で選ばれることが多く、アカデミーの修了生が最初に立つことの多い現場でもあります。',
            image: {
              src: '/images/related/the-butler/b06-tea-preparation.jpg',
              alt: '紅茶の支度をする執事（THE BUTLER の画像）'
            }
          },
          {
            title: 'プライベート・バトラー',
            desc: '特定のご家庭や個人に専属で仕える契約の形。暮らしのリズムを把握して先回りする、執事らしさが最も問われる働き方です。',
            image: {
              src: '/images/related/the-butler/b06-secretary-schedule-support.jpg',
              alt: '予定の管理を支える執事（THE BUTLER の画像）'
            }
          },
          {
            title: '専属の担当者',
            desc: '依頼から当日までを組み立てる窓口役。執事と契約の形を提案し、段取りを詰めます。依頼する側が段取りを考えなくて済むのは、この人がいるからです。',
            image: {
              src: '/images/related/the-butler/b07-anniversary-cake-gift-still.jpg',
              alt: '記念日の支度を整える執事の手元（THE BUTLER の画像）'
            }
          },
          {
            title: 'for Business',
            desc: '法人向けの枠の名前。受付やドアマン、給仕、ティーバトラー、VIPイベント、出張マナー指導など、会社の現場で執事が担う役務をまとめた呼び方です。',
            image: {
              src: '/images/related/the-butler/b06a-business-vip-attend.jpg',
              alt: 'VIPを迎える法人現場の執事（THE BUTLER の画像）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '公式サイトを読む順番——サービス、名簿、料金',
        sub: 'A reading order for the official site',
        body: [
          'THE BUTLERの公式サイトは、サービスの説明から入り、在籍執事の名簿、料金、依頼の流れへと進む構成です。アカデミーの受講生には、まず「サービス」で三つの領域を把握し、次に「名簿」で先輩たちがどんな場面を得意としているかを読むことを勧めています。',
          '名簿のページは、修了後の自分を思い描くのに最も役立つ場所です。一人ひとりの得意分野と経歴が載っていて、どの領域で力を伸ばせば選ばれる執事になれるのかが、具体的に見えてきます。',
          '料金と依頼の流れのページは、執事を頼む側の視点で読んでみてください。依頼する人が何を知りたくて、どこで迷うのか。それを知っておくことは、現場で相手の望みを先回りする練習にもなります。'
        ],
        image: {
          src: '/images/related/the-butler/b09-access-salon-tea-service.jpg',
          alt: 'お台場の執事サロンで紅茶を供する場面（THE BUTLER の画像）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師がTHE BUTLERの現場から持ち帰るもの',
        sub: 'What the field teaches the classroom',
        body: [
          'アカデミーの講師の多くは、THE BUTLERの現場に立った経験をもっています。授業で語られる失敗談や判断の話は、教科書ではなく現場から来ています。来客が予定より早く着いたときに何を優先したか、食卓で足りないものに気づいたときにどう動いたか。',
          '講師が現場で学んだ最大のことは、「正しい所作」よりも「相手に気づかれない所作」が価値をもつ、という点です。THE BUTLERの現場では、執事が目立てば目立つほど、もてなしの価値は下がります。この感覚は、教室での稽古を通じてしか身につきません。',
          'だからこそ講師は、受講生に「現場を想像しながら稽古する」ことを求めます。誰の家で、どんな来客で、何時に、どの部屋で。THE BUTLERのサービスページに載っている場面は、その想像の材料になります。'
        ],
        image: {
          src: '/images/related/the-butler/b06c-service-attire-standard.jpg',
          alt: '執事の装いの基準を示す一場面（THE BUTLER の画像）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: 'THE BUTLERは、こう誤解されやすい',
        sub: 'Where the service is misread',
        body: [
          '一つめは、「THE BUTLERは家事代行の別名だ」という理解です。家事を担うことはありますが、来客の応対や秘書業務、送迎、会社での接遇までを一人の執事が引き受け、しかも頼まれる前に整えるという姿勢で仕える点が、家事代行と決定的に違います。',
          '二つめは、「執事は指名できない」という思い込みです。THE BUTLERでは在籍執事の名簿が公開されており、依頼する側が担当者と相談しながら執事を選べます。この仕組みは2023年以降に整えられた比較的新しいものです。',
          '三つめは、「法人は使えない」という誤解です。for Businessという法人向けの枠があり、一日限りの依頼から年間契約まで幅があります。受付やドアマン、給仕、VIPイベント、出張しての指導まで、会社の現場に合わせた役務が用意されています。'
        ],
        image: {
          src: '/images/related/the-butler/b08-partc-firsttime-welcome-hero-dressfix-20260915.jpg',
          alt: '初めての依頼者を迎える執事（THE BUTLER の画像）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'BEFORE THE FIRST CALL',
        title: '初めて現場に立つ前夜、教室で最後に確かめること',
        sub: 'The last rehearsal before a first assignment',
        body: [],
        subs: [
          { t: '初めて立つ前の、仕上げの稽古', b: '修了生が初めて依頼先に立つ前、アカデミーでは仕上げの稽古をもう一度だけ行います。確かめるのは新しい技ではありません。扉の前での止まり方、名乗りの声量、先に立って歩くときの半歩の距離。教室で百回繰り返した動きが、緊張した体でも同じ形で出るかどうかだけを見ます。' },
          { t: '初日は、うまくやろうとしない', b: 'このとき講師が必ず伝えるのは、当日は上手にやろうとしなくてよい、という一言です。慣れない場で気負うと、動きは大きく速くなり、かえって目立ちます。目立たないことが仕事の質である以上、初日にすべきは、小さく静かに、教わったままを繰り返すことだけです。' },
          { t: '答えを持たないときの振る舞い', b: 'もう一つ渡すのは、その場で答えを持っていないときの振る舞いです。知らないことを取り繕わず、確かめてまいりますと告げて戻る。この一往復ができるかどうかが、初日の評価をいちばん左右します。技術ではなく、正直さの作法として教えています。' },
        ],
        image: { src: '/images/related/the-butler/afternoon-tea-service-v7-20260907-f.jpg', alt: '紅茶を供する執事の所作（THE BUTLER の公式サイトの写真）' },
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/the-butler/b07-driver-butler-car-assist.jpg', alt: '車の乗降を支える執事（THE BUTLER の画像）' },
          { src: '/images/related/the-butler/b06-tea-pouring-service.jpg', alt: '紅茶を注ぐ給仕（THE BUTLER の画像）' },
          { src: '/images/related/the-butler/b08-usecase-wedding-reception-service.jpg', alt: '結婚披露宴で給仕する執事（THE BUTLER の画像）' },
          { src: '/images/related/the-butler/b09-business-vip-event.jpg', alt: '法人のVIPイベントで控える執事（THE BUTLER の画像）' },
        ],
      },
      {
        kind: 'twocol',
        label: 'WHAT THE FIELD SENDS BACK',
        title: '現場から戻ってきた要望が、翌期の稽古を変える',
        sub: 'How the field rewrites next term',
        body: [],
        subs: [
          { t: '教室の内容は台本ではない', b: '教室の内容は、一度決めたら動かない台本ではありません。実際の依頼先で何を求められたかが講師を通じて教室へ戻り、翌期の稽古の重みづけが変わります。近年で言えば、会議の同席や来客対応といった仕事の場面が増え、応接での立ち位置や資料の渡し方に割く時間が明らかに厚くなりました。' },
          { t: '土台だけは動かさない', b: '変わらない部分もあります。一礼の深さ、器の扱い、歩幅と足音。こうした土台は、依頼の内容が変わっても要求が下がることがないため、どの期でも最初の学期で徹底して繰り返します。土台は据え置き、応用の配分だけを現場の声で入れ替える、というのが教室の設計です。' },
          { t: '机上でなく、実際の場面から逆算', b: '受講生にとって大事なのは、いま習っている稽古が机上の想定ではなく、実際に求められた場面から逆算されているという点です。学ぶ側から見れば、この往復があることが、教室と現場が地続きであることのいちばんの証拠になります。' },
        ],
        image: { src: '/images/related/the-butler/asset-ledger-review-v7-20260907-g.jpg', alt: '記録を確かめながら依頼主の用向きを整理する執事（THE BUTLER の公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'official',
        title: 'THE BUTLERの公式サイト',
        body: 'サービスの詳細、在籍執事の名簿、料金、相談の窓口は公式サイトにまとまっています。相談は無料です。執事として働く道を考えている方は、当サイトのコース案内へ。',
        url: 'https://thebutler.jp/',
        urlLabel: 'thebutler.jp',
      },
    ],
    faq: [
      { q: 'THE BUTLERは誰が運営していますか。', a: 'アカデミーの運営母体でもある一般社団法人全日本執事協会です。協会が認定した執事が、家庭と法人の両方に仕えます。' },
      { q: '家事代行との違いを一言で言うと。', a: '家事だけでなく来客応対、秘書業務、送迎、会社での接遇まで担い、しかも「頼まれる前に整え、頼まれない間は引いている」姿勢で仕える点です。' },
      { q: '執事を指名できますか。', a: 'できます。在籍執事の名簿が公開されており、専属の担当者と相談しながら候補から選べます。' },
      { q: '会社で使う場合の契約形態は。', a: '「THE BUTLER for Business」として、一日限りのスポットから年間契約まで用意されています。受付、給仕、ティーバトラー、VIPイベント、出張マナー指導などが役務です。' },
      { q: '修了後にTHE BUTLERで働く道はありますか。', a: 'THE BUTLERは協会認定の執事が働く現場のひとつで、修了と認定を経て名簿入りを目指せます。採用や登録の具体的な条件は公式サイトで確認してください。' },
      { q: '最初の相談では何を伝えればよいですか。', a: '希望の日時と、執事に任せたい場面の二つで十分です。相談は無料で、あとは専属の担当者が執事と契約の形を提案します。' },
      {
        q: 'デイ・バトラーとプライベート・バトラーの違いは。',
        a: 'デイ・バトラーは一日単位で執事を迎える形、プライベート・バトラーは特定のご家庭や個人に専属で仕える形です。専属の担当者が場面に合わせて提案します。'
      },
      {
        q: '受講生がTHE BUTLERの現場を見学することはできますか。',
        a: '見学の可否はTHE BUTLERの公式サイトの案内に従ってください。アカデミーの授業では、講師が現場の経験を教材として持ち帰り、ロールプレイの形で再現しています。'
      },
    ],
  },

  // ═══════════════ 執事ベル ═══════════════
  {
    slug: 'butler-bell',
    name: '執事ベル',
    role: '協会の頭取・執事ベルの発信',
    tagline:
      '協会の頭取である石井裕一が、執事として名乗る名前が「執事ベル」。執事文化の研究と発信、講演と研修、メディア出演を通じて、本物の執事の姿を社会に伝えています。アカデミーの教えの根には、この人の言葉があります。',
    hubImage: { src: '/images/related/butler-bell/gk-14.jpg', alt: '銀のティーセットを運ぶ執事ベル（学研撮影・執事ベルの公式サイトの写真）', position: 'center 30%' },
    heroImage: { src: '/images/related/butler-bell/gk-19.jpg', alt: '扉を開けて客を招き入れる執事ベル（学研撮影・執事ベルの公式サイトの写真）', position: 'center 35%' },
    heroTitle: '執事ベル',
    heroTitleEn: 'BUTLER BELL',
    heroLead:
      '協会の頭取・石井裕一が執事として立つときの名前が執事ベルであり、そのオフィシャルサイトです。相手を思う心と確かな技術が揃って初めて本当のもてなしになる——この考えを軸に、執事文化の研究と発信、講演や研修、メディアへの出演を続けています。アカデミーの受講生が授業で学ぶ「なぜ」の多くは、この人の言葉に遡ります。',
    officialUrl: 'https://butlerbell.jp/',
    officialLabel: '執事ベルの公式サイトを開く',
    lastUpdated: '最終更新: 2026年9月18日',
    blocks: [
      {
        kind: 'twocol',
        label: 'WHO HE IS',
        title: '西洋の執事と日本のもてなしを、一人の中で結んだ人',
        sub: 'Profile',
        body: [],
        subs: [
          { t: '頭取として広め、育てる', b: '執事ベルは、協会の頭取として日本の執事文化を広め、育てる仕事に就いています。追い求めているのは、英国流の執事の作法と、日本に根づいたもてなしの感覚を一つに溶かした、新しい執事のかたちです。' },
          { t: '心を所作で表し、独り占めしない', b: '相手を思いやる心を第一に置き、それを洗練された所作で表すこと。そして、研究と実践の中で日々学んだことを、独り占めせずに広く分かち合うこと。執事ベルのサイトは、この二つの姿勢でできています。読み物としての「執事の知恵」、映像としてのYouTube、日々の記録としてのnoteの見聞録。どれも同じ姿勢から生まれています。' },
          { t: '授業の言葉の源にあるもの', b: 'アカデミーが「執事とは何か」を教えるとき、その言葉の源にあるのは執事ベルが語ってきた考え方です。受講生にとってこのサイトは、教室で身体に覚えた所作の「なぜ」を、言葉で確かめ直せる場所でもあります。' },
        ],
        image: { src: '/images/related/butler-bell/gk-15.jpg', alt: '銀盆を手に客を待つ執事ベル（学研撮影・執事ベルの公式サイトの写真）', position: 'center 25%' },
      },
      {
        kind: 'twocol',
        label: 'THE DECISION',
        title: '閉じていた扉を、内側から開けた人',
        sub: 'Opening the door from within',
        body: [],
        subs: [
          { t: '「執事は選ぶ時代へ」', b: '2023年3月に頭取の任を受けたとき、執事ベルが最初に掲げた言葉は「執事は選ぶ時代へ」でした。それまでの協会は、執事は表に立たず、信頼できる紹介がある人としか会わないという流儀を守ってきました。その矜持は長く組織の背骨でしたが、裏を返せば、執事を必要としている人が自分では出会えない時代でもあったのです。' },
          { t: '名簿を示し、費用を隠さない', b: '誰が在籍しているのかを名簿で示し、費用を隠さず、紹介がなくても相談できる入口をつくる。執事ベルが選んだのは、長く閉じていた扉を自分の手で開けるという道でした。2025年2月には協会を一般社団法人として登記し、代表理事に就いています。' },
          { t: '割れた意見を、隠さずに語る', b: 'この判断をめぐって、会の中で意見が割れたことを執事ベルは隠しません。守りたかったものは双方とも同じだった、去った人たちの誇りもまた協会が受け継いできたものだ、と語ったうえで、執事という仕事を次の百年に渡すためにこの道を選んだと述べています。アカデミーが誰にでも開かれた学びの場であろうとするのは、この判断の延長線上にあります。' },
        ],
        image: { src: '/images/related/butler-bell/gk-16.jpg', alt: 'シルクハットを手に取る執事ベル（学研撮影・執事ベルの公式サイトの写真）' },
        reverse: true,
        tint: true,
      },
      {
        kind: 'cards',
        label: 'FOUR ROLES',
        title: '頭取・研究者・講師・語り手——執事ベルの四つの顔',
        sub: 'What he does',
        lede: '執事ベルのサイトは、自身の活動を四つに分けて紹介しています。どれも、アカデミーの教育と地続きの仕事です。',
        cards: [
          {
            title: '協会の頭取として',
            desc: '執事の技術を高め、社会的な地位を確かなものにするために、協会の活動全体を率いています。協会は1890年に創立され、2025年2月19日に法人となりました。法人での肩書は代表理事です。',
            image: { src: '/images/related/butler-bell/gk-17.jpg', alt: '正餐の間の支度を検分する執事ベル（学研撮影・執事ベルの公式サイトの写真）' },
          },
          {
            title: '執事文化の研究者として',
            desc: '西洋の執事文化と日本のもてなしを重ね合わせた独自の流儀を探り続け、その知見を「執事の知恵」、YouTubeの「執事ベル」、noteの見聞録として公開しています。',
            image: { src: '/images/related/butler-bell/gk-06.jpg', alt: '磨き上げた銀のティーセット（学研撮影・執事ベルの公式サイトの写真）' },
          },
          {
            title: '講師として',
            desc: '企業や団体から依頼を受け、もてなしやマナーを主題にした講演と研修を行っています。相手に合わせてプログラムを組み直す提案型の講座です。',
            image: { src: '/images/related/butler-bell/gk-18.jpg', alt: '卓の一皿を整える執事ベル（学研撮影・執事ベルの公式サイトの写真）' },
          },
          {
            title: '語り手として',
            desc: 'テレビ、ラジオ、雑誌、ウェブの各媒体に出演し、番組や記事の執事監修も引き受けています。テーブルマナーからビジネスマナー、ホスピタリティまで、話せる主題の幅が広いのが特徴です。',
            image: { src: '/images/related/butler-bell/gk-09.jpg', alt: '蓄音機と絵画のある館の一角（学研撮影・執事ベルの公式サイトの写真）' },
          },
        ],
      },
      {
        kind: 'twocol',
        label: 'FOUR PRINCIPLES',
        title: '心・技・成長・融合——執事ベルが語る四つの理念',
        sub: 'Heart, craft, growth, fusion',
        body: [],
        subs: [
          { t: 'ひとつめは「おもてなしの心」', b: '執事ベルは自分の理念を四つの言葉で表しています。ひとつめは「おもてなしの心」。相手を思う気持ちが最高のサービスの源であり、相手の立場に立って何を求めているかを察し、期待の少し先まで届けること。' },
          { t: '完璧な技術と、絶え間ない成長', b: 'ふたつめは「完璧な技術」。心だけでは本当のサービスにならず、確かな知識と技術に裏打ちされた所作が要ること。みっつめは「継続的な成長」。執事の道に終わりはなく、学びと自己研鑽を止めないこと。' },
          { t: '四つめは「文化の融合」', b: 'よっつめは「文化の融合」。西洋の執事文化と日本のもてなしを結び、伝統を大切にしながら現代の求めに応える新しい価値をつくること。アカデミーのカリキュラムは、この四つを教室の中で実際に稽古できる形に組み直したものです。' },
          { t: 'アカデミーの授業でいえば', b: '「心」は観察力と言葉遣いの授業に、「技」は給仕と所作の実技に、「成長」は三段のコースと認定の仕組みに、「融合」は日本の礼法と西洋のプロトコルを並べて学ぶ教程に、それぞれ対応しています。' },
        ],
        image: { src: '/images/related/butler-bell/gk-12.jpg', alt: '白手袋でカップを差し出す手元（学研撮影・執事ベルの公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'twocol',
        label: 'THE BOOK',
        title: 'Gakkenの図鑑が、装いと所作の共通の参照点になる',
        sub: 'A supervised reference',
        body: [],
        subs: [
          { t: 'Gakken の図鑑を監修', b: '執事ベルのサイトでは、Gakkenから出版された『執事のマナーと教養図鑑』が紹介されています。監修は全日本執事協会。執事の装い、立ち居振る舞い、執事文化の歴史を図版とともに整理した一冊です。' },
          { t: '装いと所作の、共通の参照点', b: 'アカデミーもこの本を、装いと所作についての共通の参照点として扱っています。昼の正装は黒のモーニングコートに黒無地の長いネクタイ、ベストはグレー。夜の最上級の装いは燕尾服に白の蝶ネクタイ。授業で教える装いの決まりは、監修の内容と揃えてあります。' },
          { t: '本の中身は公式サイトで', b: '本そのものの内容については、当サイトの「書籍紹介」と執事ベルの公式サイトで確認してください。' },
        ],
        image: { src: '/images/related/butler-bell/bell-book-billboard-20260915.jpg', alt: '『執事のマナーと教養図鑑』の紹介ビジュアル（執事ベル公式サイトの画像）' },
        tint: true,
      },
      {
        kind: 'steps',
        label: 'BUTLER WISDOM',
        title: '「執事の知恵」を、授業の予習・復習に使う',
        sub: 'Four categories of wisdom',
        lede: '執事ベルが二十年を超える経験から書きためた「執事の知恵」は、四つの分類でまとめられています。アカデミーでは、授業の前後にこれを読むことを勧めています。それぞれの分類が、どの授業と重なるのかを添えました。',
        image: { src: '/images/related/butler-bell/gk-05.jpg', alt: '三段スタンドの脇で紅茶を注ぐ（学研撮影・執事ベルの公式サイトの写真）' },
        steps: [
          { title: 'おもてなしの心', desc: 'ホスピタリティとサービスの本質を扱う分類。相手の立場に立ち、頼まれる前に察するという姿勢の根拠が語られます。観察力の授業の予習に向いています。' },
          { title: 'フォーマルマナー', desc: 'テーブルマナー、服装の決まり、社交の場での振る舞い。エッセンシャルコースの基礎科目と内容が重なります。' },
          { title: '執事スキル', desc: '観察、段取り、言葉遣い、VIPへの応対、時間の使い方。アドバンスとマスターの科目で扱う技能の背景が読めます。' },
          { title: '日常の品格', desc: '姿勢、話し方、整理整頓。執事でなくても役に立つ、日々の暮らしの中で品格を保つ考え方です。' },
        ],
      },
      {
        kind: 'band',
        label: 'WHY WE INTRODUCE HIM',
        title: '学びの場から見た、執事ベルという人',
        sub: 'Butler Bell, seen from the academy',
        body: [],
        subs: [
          { t: '協会の頭取という立場', b: '執事ベルは、アカデミーの運営母体である協会の頭取です。アカデミーで教わる「執事とは何か」「もてなしとは何か」は、この人が自分の言葉で語り、自分の身体で実践してきたことに根を張っています。' },
          { t: '身体で覚えたあとに、言葉で補う', b: '教室では、一礼の角度や、頼まれる前に動く理由を、まず身体で覚えます。執事ベルの「執事の知恵」やYouTube、noteは、その身体の記憶に言葉を添えてくれます。なぜその角度なのか、なぜ先に動くのか。授業で疑問に思ったことは、たいていこのサイトのどこかに答えがあります。' },
          { t: 'まず公式サイトを見てほしい', b: '執事という仕事に興味を持った方は、まず執事ベルの公式サイトで、執事文化の現在と、頭取が思い描く将来に触れてみてください。そのあとでアカデミーのコース案内に戻ってくると、学びの意味がはっきりするはずです。' },
        ],
        image: { src: '/images/related/butler-bell/seq-wisdom-elegance-lifestyle-b522b1.jpg', alt: '日常の品格を表す一場面（執事ベル公式サイトの画像）' },
      },
      {
        kind: 'cards',
        label: 'HOW TO USE THE SITE',
        title: '受講生が執事ベルのサイトをどう使うか',
        sub: 'For learners at each stage',
        lede: 'アカデミーの授業と執事ベルの発信は、互いを補い合う関係にあります。学びの段階ごとの使い方を四つにまとめました。',
        cards: [
          { title: '入学を迷っているとき', desc: '執事の定義と語源、日本での実像を整理した記事を読むと、この仕事を目指すかどうかを考える材料が揃います。' },
          { title: '授業で「なぜ」に出会ったとき', desc: '「執事の知恵」の四分類は、教室で身体に覚えた所作の背景を言葉で補ってくれます。' },
          { title: '修了して現場に出たあと', desc: '「執事の道に終わりはない」という理念は、修了後も稽古を続ける修了生の支えになります。' },
          { title: '企業として研修を考えるとき', desc: 'もてなしやマナーを主題にした講演と研修は、執事ベルの公式サイトから相談できます。アカデミーの法人研修と組み合わせる方もいます。' },
        ],
        tint: true,
      },
{
        kind: 'cards',
        label: 'GLOSSARY',
        title: '執事ベルのサイトに出てくる四つの言葉',
        sub: 'Four terms from the Butler Bell site',
        lede: '執事ベルのサイトを初めて読む受講生のために、繰り返し出てくる言葉の意味を整理しました。',
        cards: [
          {
            title: '執事の知恵',
            desc: '執事ベルが二十年を超える経験から書きためた読み物の総称。おもてなしの心、フォーマルマナー、執事スキル、日常の品格の四分類があります。',
            image: {
              src: '/images/related/butler-bell/seq-daily-support-butler-1-62fe23.jpg',
              alt: '日常を支える執事の一場面（執事ベル公式サイトの画像）'
            }
          },
          {
            title: '見聞録',
            desc: 'noteで公開されている、執事ベルの日々の記録です。YouTubeが映像なら、見聞録は文章。同じ姿勢から生まれた二つの発信です。',
            image: {
              src: '/images/related/butler-bell/seq-tea-butler-6-e68f76.jpg',
              alt: '紅茶を供する執事（執事ベル公式サイトの画像）'
            }
          },
          {
            title: '執事監修',
            desc: 'テレビや雑誌、ウェブの企画で、執事の描かれ方が正しいかを確かめる仕事です。執事ベルはメディア出演と併せてこれを引き受けています。',
            image: {
              src: '/images/related/butler-bell/seq-event-butler-2-80fb86.jpg',
              alt: '催しの場で控える執事（執事ベル公式サイトの画像）'
            }
          },
          {
            title: '頭取',
            desc: '協会の長の呼び名で、執事ベルの本業の肩書です。法人としては代表理事にあたります。',
            image: {
              src: '/images/related/butler-bell/seq-executive-butler-4-ce1287.jpg',
              alt: '経営者のそばで働く執事（執事ベル公式サイトの画像）'
            }
          }
        ],
        tint: true
      },
{
        kind: 'twocol',
        label: 'READING THE SITE',
        title: '執事ベルのサイトを、授業と並行して読む',
        sub: 'How to pair the site with your classes',
        body: [
          '執事ベルのサイトは、プロフィール、活動内容、執事の知恵、サービス、メディアという構成です。受講生には、まずプロフィールで頭取の考え方を読み、次に「執事の知恵」を授業の進度に合わせて拾い読みすることを勧めています。',
          'エッセンシャルコースの間は「フォーマルマナー」と「日常の品格」を、アドバンス以降は「執事スキル」を、そして全期間を通じて「おもてなしの心」を。四分類は、アカデミーの三段のコースにきれいに重なります。',
          'サービスとメディアのページは、執事という仕事が社会のどこまで広がっているかを知る材料です。講演や研修、番組の監修。教室の外で執事の知見がどう使われているかを、ここで確かめてください。'
        ],
        image: {
          src: '/images/related/butler-bell/seq-contact-hero-desk-e07c81.jpg',
          alt: '執事ベルのサイトの問い合わせ窓口を象徴する机（執事ベル公式サイトの画像）'
        },
        reverse: true
      },
{
        kind: 'twocol',
        label: 'FROM THE INSTRUCTORS',
        title: '講師が執事ベルの言葉をどう使うか',
        sub: 'Bell words in the classroom',
        body: [
          'アカデミーの講師は、執事ベルの四つの理念を授業の道具として使います。所作を教えるときは「完璧な技術」、観察を教えるときは「おもてなしの心」、修了間近の受講生には「継続的な成長」。理念は掲示物ではなく、日々の稽古の中で口にされる言葉です。',
          '講師が特に大切にしているのは、「心だけでは真のサービスにならない」という一文です。気持ちがあっても手が動かなければ、相手には届かない。この考え方があるから、アカデミーは実技の反復に多くの時間を割きます。',
          '同時に講師は、「執事は選ぶ時代へ」という言葉も受講生に伝えます。閉じていた扉を開けたのは頭取の決断ですが、開いた扉の向こうで選ばれるかどうかは、一人ひとりの稽古にかかっています。'
        ],
        image: {
          src: '/images/related/butler-bell/seq-table-manner-cad0af.jpg',
          alt: 'テーブルマナーを扱う一場面（執事ベル公式サイトの画像）'
        },
        tint: true
      },
{
        kind: 'twocol',
        label: 'COMMON MISREADINGS',
        title: '執事ベルという人物への、よくある思い込み',
        sub: 'Assumptions people bring to him',
        body: [
          '一つめは、「執事ベルは会社の名前だ」という理解です。執事ベルは人の名前——協会の頭取・石井裕一が執事として名乗る名前——であり、そのオフィシャルサイトの名前でもあります。',
          '二つめは、「執事ベルのサイトで執事を頼める」という思い込みです。執事を家庭や会社に迎えたい場合の窓口は、協会が運営するTHE BUTLERです。執事ベルのサイトで受け付けているのは、講演・研修とメディア出演の依頼です。',
          '三つめは、「執事の知恵は執事志望者だけのもの」という見方です。四分類のうち「日常の品格」や「フォーマルマナー」は、執事でなくても役立つ内容として書かれています。家族や友人に勧めても、そのまま読めます。'
        ],
        image: {
          src: '/images/related/butler-bell/seq-vip-service-a0ca35.jpg',
          alt: 'VIPに仕える執事の一場面（執事ベル公式サイトの画像）'
        },
        reverse: true
      },
      {
        kind: 'twocol',
        label: 'HIS CLASSROOM',
        title: '校長が教壇に立つ日の、教室の静けさ',
        sub: 'The room on the days he teaches',
        body: [],
        subs: [
          { t: '声を張らない教室', b: '校長が自ら教壇に立つ日、教室はいつもより静かになります。声を張らないからです。指示は短く、実演が長い。説明を聞いて理解する時間より、目の前で一度やって見せられたものを真似る時間のほうが長く取られます。言葉を尽くすより、形を見せるほうが速いという考え方が、そのまま授業の組み立てになっています。' },
          { t: '直しは、一点ずつ渡される', b: '直しの入り方にも特徴があります。全体を否定せず、変える箇所を一つだけ告げる。手の高さをあと少し、と言われた受講生は、その一点だけを直してもう一度通します。直った瞬間に次の一点が渡される。この刻み方のおかげで、受講生は自分がどこまで出来ていて、次に何をすればいいかを見失いません。' },
          { t: '言葉ではなく、良い形で終える', b: '授業の終わりに感想を求められることはほとんどありません。代わりに、もう一度だけ通しますか、と問われます。稽古は言葉で締めくくるものではなく、最後に良い形を体に残して終えるもの——そういう終わり方を、受講生は自然に覚えていきます。' },
        ],
        image: { src: '/images/related/butler-bell/gk-04.jpg', alt: '所作を実演して見せる場面（執事ベルの公式サイトの写真）' },
      },
      {
        kind: 'twocol',
        label: 'WORDS HE REPEATS',
        title: '繰り返される言い回しを、受講生の言葉で書き留める',
        sub: 'Phrases that come back every term',
        body: [],
        subs: [
          { t: '相手の手が止まる前に動け', b: '同じ言い回しが、期をまたいで何度も出てきます。一つは、相手の手が止まる前に動け、というもの。頼まれてから動くのでは遅く、頼む必要をなくすことが仕事だ、という意味です。受講生はこれを最初は心構えとして聞き、稽古が進むにつれて、視線と手元を見る具体的な観察の話だったと気づきます。' },
          { t: 'うまくやるより、同じにやれ', b: 'もう一つは、うまくやるより、同じにやれ。日によって出来が変わる所作は、相手にとっては読めない動きになります。上手な日と下手な日の差を小さくすることのほうが、最高到達点を上げることより先だ、という順番が、この一言に入っています。' },
          { t: '見えないところを整えてから', b: '三つめは、見えないところを整えてから前に出ろ。控えの場の並べ方や、盆の上の向きといった、客の目に触れない部分の手入れを指しています。表に出る所作は、見えない支度の結果でしかない——校長がいちばん多く口にするのは、この一文です。' },
        ],
        image: { src: '/images/related/butler-bell/gk-01.jpg', alt: '箱に納められた懐中時計（執事ベルの公式サイトの写真）' },
        reverse: true,
      },
      {
        kind: 'gallery',
        images: [
          { src: '/images/related/butler-bell/bell-book-ch1-dress-20260915.jpg', alt: '書籍の第一章「装い」の紹介ビジュアル（執事ベル公式サイトの画像）' },
          { src: '/images/related/butler-bell/seq-etiquette-table-manners-butler-1-7a8e68.jpg', alt: 'テーブルマナーを示す執事（執事ベル公式サイトの画像）' },
          { src: '/images/related/butler-bell/seq-private-butler-hero-hands-b46a9f.jpg', alt: 'プライベートバトラーの手元（執事ベル公式サイトの画像）' },
          { src: '/images/related/butler-bell/seq-manner-course-elegant-841489.jpg', alt: 'マナー講座の一場面（執事ベル公式サイトの画像）' },
        ],
      },
      {
        kind: 'official',
        title: '執事ベルの公式サイト',
        body: 'プロフィール、「執事の知恵」、講演・研修とメディア出演の依頼、書籍の案内は執事ベルの公式サイトへ。執事を学びたい方は、当サイトのコース案内へどうぞ。',
        url: 'https://butlerbell.jp/',
        urlLabel: 'butlerbell.jp',
      },
    ],
    faq: [
      { q: '「執事ベル」は本名ですか。', a: '協会の頭取・石井裕一が執事として活動するときの名前です。公式サイトもこの名前で運営されています。' },
      { q: 'アカデミーとはどう関わっていますか。', a: 'アカデミーの運営母体である協会の頭取です。アカデミーが教える執事観やもてなしの考え方は、執事ベルの言葉と実践に根ざしています。' },
      { q: '「執事は選ぶ時代へ」とは。', a: '2023年3月の頭取就任にあたって掲げた言葉です。在籍執事の名簿を公開し、費用を明らかにし、紹介がなくても相談できる窓口を設けるという方針を表しています。' },
      { q: '講演や研修は頼めますか。', a: '企業や団体からの依頼に応じて、もてなしやマナーを主題にした講演と研修を行っています。公式サイトの問い合わせフォームから相談できます。' },
      { q: 'メディア出演の窓口は。', a: 'テレビ、ラジオ、雑誌、ウェブでの出演や取材、執事監修は公式サイトのフォームで受け付けています。企画の内容、希望日時、媒体名、担当者の連絡先を添えてください。' },
      { q: '『執事のマナーと教養図鑑』はどんな本ですか。', a: 'Gakkenから出版され、全日本執事協会が監修した図鑑です。執事の装いと所作、執事文化の歴史を図版つきで整理しています。詳細は当サイトの書籍紹介と執事ベルの公式サイトへ。' },
      {
        q: '執事ベルのサイトから執事を依頼できますか。',
        a: 'できません。執事を迎える窓口は協会が運営するTHE BUTLERです。執事ベルのサイトが受け付けているのは、講演・研修とメディア出演・監修の依頼です。'
      },
      {
        q: 'YouTubeの「執事ベル」と見聞録は何が違いますか。',
        a: 'YouTubeは映像、noteの見聞録は文章による発信です。どちらも執事文化の研究と実践から生まれた知見を分かち合うという同じ姿勢で続けられています。'
      },
    ],
  },
];

export default sitesA;
